/**
 * InitializeVariableField - Variable initialization editor with add/remove capabilities
 *
 * Allows users to initialize up to 20 variables, each with:
 * - Name (string input)
 * - Type (dropdown: Boolean, Integer, Float, String, Object, Array)
 * - Value (type-dependent input - boolean gets dropdown, others get text)
 *
 * Based on LogicAppsUX InitializeVariableEditor pattern.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  mergeClasses,
  Label,
  Text,
  Button,
  Tooltip,
  Dropdown,
  Option,
  Badge,
} from '@coreai/fluentui-react';
import {
  DeleteFilled,
  AddFilled,
  ChevronDownFilled,
  ChevronRightFilled,
} from '@fluentui/react-icons';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';
import type { ValueSegment } from '@/types/valueSegment';
import type { AgentParameterSchema } from './AgentParameterMenu';
import { TokenizedInput } from './TokenizedInput';
import { ExpressionPreview } from './ExpressionPreview';
import { VariableValueInput, VARIABLE_TYPE, coerceVariableValue, type VariableType } from './VariableValueInput';
import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const MAX_VARIABLES = 20;

const variableTypeOptions: { key: VariableType; label: string }[] = [
  { key: VARIABLE_TYPE.BOOLEAN, label: 'Boolean' },
  { key: VARIABLE_TYPE.INTEGER, label: 'Integer' },
  { key: VARIABLE_TYPE.FLOAT, label: 'Float' },
  { key: VARIABLE_TYPE.STRING, label: 'String' },
  { key: VARIABLE_TYPE.OBJECT, label: 'Object' },
  { key: VARIABLE_TYPE.ARRAY, label: 'Array' },
];

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  variableList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  variableCard: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    // Don't use overflow: hidden - it clips the preview overlay
  },
  variableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  variableHeaderCollapsed: {
    borderBottom: 'none',
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
  },
  variableHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flex: 1,
    overflow: 'hidden',
  },
  expandButton: {
    minWidth: 'auto',
    width: '24px',
    height: '24px',
    padding: 0,
    flexShrink: 0,
  },
  variableName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  variableTypeBadge: {
    flexShrink: 0,
  },
  variableHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  deleteButton: {
    minWidth: 'auto',
    height: '32px',
    padding: tokens.spacingHorizontalS,
  },
  variableContent: {
    padding: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  fieldRequired: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  fieldInputWrapper: {
    position: 'relative',
  },
  typeDropdown: {
    width: '100%',
  },
  booleanDropdown: {
    width: '100%',
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontStyle: 'italic',
  },
});

/** Variable item state */
interface VariableItem {
  id: string;
  name: string;
  type: VariableType;
  value: string;
  isExpanded: boolean;
}

/** The output format for variables array */
interface VariableOutput {
  name: string;
  type: VariableType;
  value?: unknown;
}

export interface InitializeVariableFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[] | VariableOutput[];
  onChange: (value: VariableOutput[]) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Show the agent parameter button - for tools within an agent */
  showAgentParameterButton?: boolean | undefined;
  /** The agent parameter schema from the tool definition */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs from the last successful run, keyed by action name */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available for previews */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since the last run (data may be stale) */
  isStale?: boolean | undefined;
}

/**
 * Parse value into VariableOutput array
 */
function parseValueToVariables(value: string | ValueSegment[] | VariableOutput[]): VariableOutput[] {
  // Direct array of VariableOutput
  if (Array.isArray(value) && !isValueSegmentArray(value)) {
    return value as VariableOutput[];
  }

  // ValueSegment array - convert to string first
  const stringValue = isValueSegmentArray(value)
    ? segmentsToString(value)
    : typeof value === 'string'
      ? value
      : '';

  if (!stringValue || stringValue.trim() === '') {
    return [];
  }

  try {
    const parsed = JSON.parse(stringValue);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return [];
  } catch {
    return [];
  }
}

/**
 * Convert VariableOutput[] to VariableItem[] for editing
 */
function variablesToItems(variables: VariableOutput[]): VariableItem[] {
  return variables.map((v) => ({
    id: generateSegmentId(),
    name: v.name || '',
    type: (v.type as VariableType) || VARIABLE_TYPE.STRING,
    value: v.value !== undefined
      ? (typeof v.value === 'string' ? v.value : JSON.stringify(v.value))
      : '',
    isExpanded: true,
  }));
}

/**
 * Convert VariableItem[] to VariableOutput[] for saving
 */
function itemsToVariables(items: VariableItem[]): VariableOutput[] {
  return items
    .filter((item) => item.name.trim()) // Only include items with a name
    .map((item) => {
      const output: VariableOutput = {
        name: item.name.trim(),
        type: item.type,
      };

      // Only add value if it's not empty
      if (item.value.trim()) {
        output.value = coerceVariableValue(item.type, item.value);
      }

      return output;
    });
}

/**
 * Get a display name for a variable (for collapsed header)
 */
function getVariableDisplayName(item: VariableItem): string {
  return item.name.trim() || 'New Variable';
}

/**
 * Get the display label for a variable type
 */
function getTypeLabel(type: VariableType): string {
  return variableTypeOptions.find((opt) => opt.key === type)?.label || type;
}

export const InitializeVariableField = ({
  name,
  label,
  value,
  onChange,
  description,
  required,
  disabled = false,
  error,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData,
  isStale,
}: InitializeVariableFieldProps) => {
  const styles = useStyles();

  // Parse initial value only once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialVariables = useMemo(() => parseValueToVariables(value), []);

  // Internal items state
  const [items, setItems] = useState<VariableItem[]>(() => {
    const parsed = variablesToItems(initialVariables);
    // Start with one empty variable if none exist
    if (parsed.length === 0) {
      return [{
        id: generateSegmentId(),
        name: '',
        type: VARIABLE_TYPE.STRING,
        value: '',
        isExpanded: true,
      }];
    }
    return parsed;
  });

  // Track if we initiated the change (to avoid re-parsing our own updates)
  const isInternalChangeRef = useRef(false);

  // Track the last output we sent
  const lastOutputRef = useRef<VariableOutput[]>(itemsToVariables(items));

  // Track focus for preview
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Sync from external value changes only
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    const externalVariables = parseValueToVariables(value);
    const externalJson = JSON.stringify(externalVariables);
    const lastJson = JSON.stringify(lastOutputRef.current);

    // Only re-parse if the external value is different from what we last sent
    if (externalJson !== lastJson) {
      const newItems = variablesToItems(externalVariables);
      if (newItems.length === 0) {
        setItems([{
          id: generateSegmentId(),
          name: '',
          type: VARIABLE_TYPE.STRING,
          value: '',
          isExpanded: true,
        }]);
      } else {
        setItems(newItems);
      }
      lastOutputRef.current = externalVariables;
    }
  }, [value]);

  // Notify parent of changes
  const notifyChange = useCallback((newItems: VariableItem[]) => {
    const variables = itemsToVariables(newItems);
    lastOutputRef.current = variables;
    isInternalChangeRef.current = true;
    onChange(variables);
  }, [onChange]);

  // Handle name change
  const handleNameChange = useCallback((itemId: string, newName: string) => {
    setItems((prev) => {
      const newItems = prev.map((item) =>
        item.id === itemId ? { ...item, name: newName } : item
      );
      notifyChange(newItems);
      return newItems;
    });
  }, [notifyChange]);

  // Handle type change
  const handleTypeChange = useCallback((itemId: string, newType: VariableType) => {
    setItems((prev) => {
      const newItems = prev.map((item) => {
        if (item.id !== itemId) return item;
        // Clear value when type changes (except for simple type changes)
        const shouldClearValue = item.type !== newType && (
          newType === VARIABLE_TYPE.BOOLEAN ||
          item.type === VARIABLE_TYPE.BOOLEAN ||
          newType === VARIABLE_TYPE.OBJECT ||
          newType === VARIABLE_TYPE.ARRAY
        );
        return {
          ...item,
          type: newType,
          value: shouldClearValue ? '' : item.value,
        };
      });
      notifyChange(newItems);
      return newItems;
    });
  }, [notifyChange]);

  // Handle value change
  const handleValueChange = useCallback((itemId: string, newValue: string) => {
    setItems((prev) => {
      const newItems = prev.map((item) =>
        item.id === itemId ? { ...item, value: newValue } : item
      );
      notifyChange(newItems);
      return newItems;
    });
  }, [notifyChange]);

  // Toggle item expansion
  const toggleExpand = useCallback((itemId: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, isExpanded: !item.isExpanded } : item
      )
    );
  }, []);

  // Add new variable
  const handleAddVariable = useCallback(() => {
    if (items.length >= MAX_VARIABLES) return;
    setItems((prev) => {
      const newItems = [
        ...prev,
        {
          id: generateSegmentId(),
          name: '',
          type: VARIABLE_TYPE.STRING as VariableType,
          value: '',
          isExpanded: true,
        },
      ];
      notifyChange(newItems);
      return newItems;
    });
  }, [items.length, notifyChange]);

  // Remove variable
  const handleRemoveVariable = useCallback((itemId: string) => {
    setItems((prev) => {
      // Don't allow removing the last variable
      if (prev.length <= 1) return prev;
      const newItems = prev.filter((item) => item.id !== itemId);
      notifyChange(newItems);
      return newItems;
    });
  }, [notifyChange]);

  // Render value input based on type
  const renderValueInput = (item: VariableItem) => {
    const fieldId = `${name}_${item.id}_value`;
    return (
      <VariableValueInput
        fieldId={fieldId}
        type={item.type}
        value={item.value}
        segmentId={item.id}
        onChange={(v) => handleValueChange(item.id, v)}
        disabled={disabled}
        showAgentParameterButton={showAgentParameterButton}
        agentParameterSchema={agentParameterSchema}
        actionOutputs={actionOutputs}
        hasRunData={hasRunData}
        isStale={isStale}
      />
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
      </div>

      {description && (
        <Text className={styles.description}>{description}</Text>
      )}

      <div className={styles.variableList}>
        {items.map((item) => (
          <div key={item.id} className={styles.variableCard}>
            {/* Header */}
            <div
              className={mergeClasses(styles.variableHeader, !item.isExpanded && styles.variableHeaderCollapsed)}
              onClick={() => toggleExpand(item.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleExpand(item.id);
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={item.isExpanded}
            >
              <div className={styles.variableHeaderLeft}>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={item.isExpanded ? <ChevronDownFilled /> : <ChevronRightFilled />}
                  className={styles.expandButton}
                  aria-label={item.isExpanded ? 'Collapse' : 'Expand'}
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(item.id);
                  }}
                />
                <Text className={styles.variableName}>
                  {getVariableDisplayName(item)}
                </Text>
              </div>
              <div className={styles.variableHeaderRight}>
                {item.type && (
                  <Badge
                    appearance="filled"
                    size="medium"
                    color="brand"
                    className={styles.variableTypeBadge}
                  >
                    {getTypeLabel(item.type)}
                  </Badge>
                )}
                <Tooltip
                  content={items.length <= 1 ? "Cannot delete the last variable" : "Delete variable"}
                  relationship="label"
                >
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DeleteFilled />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveVariable(item.id);
                    }}
                    disabled={disabled || items.length <= 1}
                    className={styles.deleteButton}
                    aria-label="Delete variable"
                  />
                </Tooltip>
              </div>
            </div>

            {/* Content */}
            {item.isExpanded && (
              <div className={styles.variableContent}>
                {/* Name field */}
                <div className={styles.fieldContainer}>
                  <Label className={styles.fieldLabel}>
                    Name
                    <span className={styles.fieldRequired}>*</span>
                  </Label>
                  <div
                    className={styles.fieldInputWrapper}
                    onFocus={() => setFocusedField(`${name}_${item.id}_name`)}
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setFocusedField(null);
                      }
                    }}
                  >
                    <TokenizedInput
                      name={`${name}_${item.id}_name`}
                      value={[{
                        id: `${item.id}_name`,
                        type: ValueSegmentType.LITERAL,
                        value: item.name,
                      }]}
                      onChange={(segments) => handleNameChange(item.id, segmentsToString(segments))}
                      placeholder="Enter variable name"
                      disabled={disabled}
                      showAgentParameterButton={showAgentParameterButton}
                      agentParameterSchema={agentParameterSchema}
                      actionOutputs={actionOutputs}
                    />
                    {focusedField === `${name}_${item.id}_name` && item.name && (
                      <ExpressionPreview
                        value={item.name}
                        actionOutputs={actionOutputs}
                        hasRunData={hasRunData}
                        isStale={isStale}
                        mode="overlay"
                      />
                    )}
                  </div>
                </div>

                {/* Type field */}
                <div className={styles.fieldContainer}>
                  <Label className={styles.fieldLabel}>
                    Type
                    <span className={styles.fieldRequired}>*</span>
                  </Label>
                  <Dropdown
                    className={styles.typeDropdown}
                    value={getTypeLabel(item.type)}
                    selectedOptions={[item.type]}
                    onOptionSelect={(_, data) => {
                      if (data.optionValue) {
                        handleTypeChange(item.id, data.optionValue as VariableType);
                      }
                    }}
                    placeholder="Select variable type"
                    disabled={disabled}
                  >
                    {variableTypeOptions.map((opt) => (
                      <Option key={opt.key} value={opt.key}>
                        {opt.label}
                      </Option>
                    ))}
                  </Dropdown>
                </div>

                {/* Value field */}
                <div className={styles.fieldContainer}>
                  <Label className={styles.fieldLabel}>Value</Label>
                  {renderValueInput(item)}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Add variable button */}
        <Button
          appearance="subtle"
          size="small"
          icon={<AddFilled />}
          onClick={handleAddVariable}
          disabled={disabled || items.length >= MAX_VARIABLES}
          className={styles.addButton}
        >
          Add Variable
        </Button>
      </div>

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

InitializeVariableField.displayName = 'InitializeVariableField';
